// Service worker registration + update flow.
//
// In dev the worker registers as `/sw.js?dev` and runs as a pass-through (see
// public/sw.js). In production a newly installed worker does NOT take over the
// page automatically — it waits until the user accepts an update, so the
// controller is never swapped under a page that already loaded the old assets.

import { createTranslator, getInitialAppLanguage } from './i18n';
import { readStoredAppLanguage } from './features/app/language-storage';
import { startClientRecoveryChecks } from './client-recovery';

const DEV_WORKER_URL = '/sw.js?dev';
const PROD_WORKER_URL = '/sw.js';
let shouldReloadOnControllerChange = false;

function promptUpdate(waitingWorker: ServiceWorker) {
  // This banner is built outside React, so resolve the translator from the
  // stored language preference (falling back to the browser language).
  const t = createTranslator(getInitialAppLanguage(readStoredAppLanguage()));
  const banner = document.createElement('div');
  banner.setAttribute('role', 'status');
  banner.style.cssText = [
    'position:fixed',
    'inset-inline:0',
    'bottom:0',
    'z-index:2147483647',
    'display:flex',
    'gap:12px',
    'align-items:center',
    'justify-content:center',
    'flex-wrap:wrap',
    'padding:12px 16px',
    'font:500 14px/1.4 Inter,ui-sans-serif,system-ui,sans-serif',
    'color:#f7f5ef',
    'background:#17211b',
  ].join(';');

  const label = document.createElement('span');
  label.textContent = t('A new version of Forma is available.');

  const refresh = document.createElement('button');
  refresh.textContent = t('Refresh');
  refresh.style.cssText = [
    'border:0',
    'border-radius:9999px',
    'padding:6px 16px',
    'font:inherit',
    'font-weight:600',
    'color:#17211b',
    'background:#f7f5ef',
    'cursor:pointer',
  ].join(';');
  refresh.addEventListener('click', () => {
    refresh.disabled = true;
    shouldReloadOnControllerChange = true;
    waitingWorker.postMessage('SKIP_WAITING');
  });

  banner.append(label, refresh);
  document.body.append(banner);
}

function watchForUpdates(registration: ServiceWorkerRegistration) {
  // A worker may already be waiting when we register (installed in a previous
  // session/tab).
  if (registration.waiting && navigator.serviceWorker.controller) {
    promptUpdate(registration.waiting);
  }

  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;

    if (!installing) {
      return;
    }

    installing.addEventListener('statechange', () => {
      // Only prompt for an *update* — the first-ever install has no controller
      // and should activate silently.
      if (
        installing.state === 'installed' &&
        navigator.serviceWorker.controller
      ) {
        promptUpdate(installing);
      }
    });
  });
}

export function registerServiceWorker() {
  // Staging always loads through its password gate, including after refresh.
  if (import.meta.env.MODE === 'staging') {
    return;
  }

  const isDev = import.meta.env.DEV;

  if (!isDev) {
    startClientRecoveryChecks();
  }

  if (!('serviceWorker' in navigator)) {
    return;
  }

  const workerUrl = isDev ? DEV_WORKER_URL : PROD_WORKER_URL;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(workerUrl, { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        if (!isDev) {
          watchForUpdates(registration);
        }
      })
      .catch((error: unknown) => {
        console.error('Service worker registration failed:', error);
      });
  });

  if (isDev) {
    return;
  }

  // When the newly activated worker takes control (after the user accepts),
  // reload once so the page runs against the new assets.
  let hasReloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!shouldReloadOnControllerChange || hasReloaded) {
      return;
    }

    hasReloaded = true;
    window.location.reload();
  });
}
